/* eslint-disable react/display-name */
/**
 *
 * ReportDetails
 *
 */

import { memo, useState } from 'react';

import {
  Combobox,
  DateCell,
  Datepicker,
  dateTimeFormatter,
  formattedCell,
  type HookFormMethods,
  HookFormResolver,
  Number,
  PageTitle,
  Text
} from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import GridApplication from 'app/components/grid/gridApplication';
import { baseUrlReports } from 'app/modules/Reports/http';
import { type TaxReportGeneralInfo } from 'app/modules/Reports/types';
import { useGetAllReports } from 'app/modules/taxes/ReportOperation/services/hooks';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ActionsGrid } from '../../../components/grid';
import { type ActionColumn } from '../../../components/grid/actions-column';
import { useOperationsList } from '../../Operations/EconomyActivity/hooks/useOperationsList';
import { useCountries } from '../TaxReturnList/hooks';
import { label, messages } from '../TaxReturnList/translation';

interface Props {}
export interface SearchCatalogType {
  country: string;
  period: number;
  periodFrom?: Date;
  periodTo?: Date;
  reportId: string;
  reportNumber: string;
  tin: string;
  report: any;
  countryObj: any;
  operation: any;
  OperationId: string;
}
export const ReportDetails = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState<any>({ summaryMode: true });
  const { isLoading: loadingContries, data: countries } = useCountries();
  const { data: reports, loading: loadingReports, error: errorReports } = useGetAllReports();
  const [{ data: operations, loading: loadingOperations, error: errroOperations }] =
    useOperationsList();
  const onSearch = (values: SearchCatalogType) => {
    values.reportId = values.report?.id;
    values.country = values.countryObj?.code;
    values.OperationId = values.operation?.id;
    setSearch({ summaryMode: true, ...values });
  };
  const navigate = useNavigate();
  const actions: Array<ActionColumn> = [
    {
      icon: Icon.Search,
      variant: 'secondary',
      onClick: (row: TaxReportGeneralInfo) => {
        navigate('/reports/details/' + row.id);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: t('common.label.actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    {
      headerName: t('report-details.label.reportNumber'),
      field: 'taxReportNumber'
    },
    {
      headerName: t('config.tin'),
      field: 'taxpayer.nit',
      valueGetter: (params: any) => {
        return params.data.taxpayer.nit;
      }
    },
    {
      headerName: t('report-details.label.taxpayerName'),
      field: 'taxpayer.name',
      valueGetter: (params: any) => {
        return params.data.taxpayer.name;
      }
    },
    {
      headerName: t('report-details.label.type'),
      field: 'taxReportType'
    },
    {
      headerName: t('report-details.label.taxReportState'),
      field: 'taxReportState',
      valueFormatter: (param) => {
        return t(`report-details.stateType.${param.value}`);
      }
    },
    {
      headerName: t('report-details.label.active'),
      field: 'active',
      valueFormatter: (params: ValueFormatterParams) => {
        return t(`common.yes-no.${params.data?.active ? 'yes' : 'no'}`);
      }
    },
    {
      headerName: t('report-details.label.registrationDate'),
      field: 'createdDate',
      ...formattedCell(dateTimeFormatter, DateCell)
    },
    {
      headerName: t('report-details.label.period'),
      field: 'period'
    },

    {
      headerName: t('report-details.label.reportType'),
      field: 'reportVersion.report.name',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data.reportVersion.report.name;
      }
    }
  ];

  return (
    <>
      <PageTitle title={t('report-details.title.page')} />
      <HookFormResolver onSubmit={onSearch}>
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Combobox
                    data={!loadingReports ? reports : []}
                    code={'id'}
                    display={'shortName'}
                    label={t('report-details.label.reportType')}
                    name="report"
                  />
                </Col>
                <Col>
                  <Number
                    description={messages(t, 'periodMessage')}
                    label={label(t, 'period')}
                    name="period"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Combobox
                    data={!loadingContries ? countries : []}
                    code={'code'}
                    display={'name'}
                    label={label(t, 'country')}
                    name="countryObj"
                  />
                </Col>
                <Col>
                  <Combobox
                    data={operations}
                    code={'id'}
                    display={'name'}
                    label={label(t, 'taxreturnType')}
                    name="operation"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text label={t('config.tin')} name="nit" />
                </Col>
                <Col>
                  <Text label={t('report-details.label.reportNumber')} name="reportNumber" />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Datepicker label={label(t, 'periodFrom')} name="periodFrom" />
                </Col>
                <Col>
                  <Datepicker label={label(t, 'periodTo')} name="periodTo" />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text label={t('report-details.label.taxpayerName')} name="taxpayerName" />
                </Col>
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    variant={'secondary'}
                    type={'reset'}
                    onClick={() => {
                      setSearch({ summaryMode: true });
                      reset({
                        operation: null,
                        countryObj: null,
                        report: null,
                        periodFrom: null,
                        periodTo: null
                      });
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
      <hr />
      <Row>
        <Col>
          <GridApplication
            columnDefs={colDef}
            url={`${baseUrlReports}/tax-reports`}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
});
